import { InputAdornment, Stack, Typography } from '@mui/material'
import React, { FC, forwardRef, useEffect, useState } from 'react'
import {
  Button,
  Divider,
  IconButton,
  TextInputField,
  TextInputFieldProps,
} from 'utoniq-ui'

import { config } from '@/config'
import {
  Form,
  formRegex,
  tLabel,
  useForm,
  UseFormProps,
  useTranslation,
} from '@/core'
import { Cell, CellFrame } from '@/core/architecture'
import { getPlatform } from '@/core/native/app'

export type SignUpArtistFormProps = {
  invitationCode?: string
  onSignUp: (values: SignUpFormValues) => Promise<void>
  onSignUpWithTwitter: SocialSignUpCellProps['onSignUpWithTwitter']
  onSignUpWithApple: SocialSignUpCellProps['onSignUpWithApple']
  onSignUpWithGoogle: SocialSignUpCellProps['onSignUpWithGoogle']
}

type SignUpFormValues = {
  displayName: string
  accountId: string
  email: string
  password: string
  invitationCode: string
}

const schema: UseFormProps['schema'] = o => ({
  displayName: o.string().max(50).label(tLabel('signUpArtist.displayName')),
  accountId: o
    .string()
    .pattern(new RegExp(/^[a-zA-Z0-9_]*$/))
    .max(20)
    .label(tLabel('signUpArtist.accountId')),
  email: o.string().regex(formRegex.email).label(tLabel('signUpArtist.email')),
  password: o.string().min(8).max(30).label(tLabel('signUpArtist.password')),
  invitationCode: o
    .string()
    .length(8)
    .optional()
    .allow('')
    .label(tLabel('signUpArtist.invitationCode')),
})

export const SignUpArtistForm: FC<SignUpArtistFormProps> = ({
  invitationCode,
  onSignUp,
  onSignUpWithTwitter,
  onSignUpWithApple,
  onSignUpWithGoogle,
}) => {
  const { t } = useTranslation('signUpArtist')

  const {
    handleSubmit,
    register,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<SignUpFormValues>({
    schema,
  })

  useEffect(() => {
    if (invitationCode) {
      reset({ invitationCode })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invitationCode])

  return (
    <Stack sx={{ pb: 4, px: 5 }}>
      {/**
       * @description
       * Open when function is implemented in the future
       * https://app.clickup.com/t/908127/UT-6118
       */}
      {/*<SocialSignUpCell*/}
      {/*  onSignUpWithApple={onSignUpWithApple}*/}
      {/*  onSignUpWithGoogle={onSignUpWithGoogle}*/}
      {/*  onSignUpWithTwitter={onSignUpWithTwitter}*/}
      {/*/>*/}
      {/*<SupplementTextCell />*/}
      <Form onSubmit={handleSubmit(onSignUp)} sx={{ pt: 3 }}>
        <TextInputCell
          type="text"
          error={errors?.displayName}
          {...register('displayName')}
        />
        <TextInputCell
          type="text"
          error={errors?.accountId}
          {...register('accountId')}
        />
        <TextInputCell
          type="email"
          error={errors?.email}
          {...register('email')}
        />
        <PasswordInputCell error={errors?.password} {...register('password')} />
        <TextInputCell
          error={errors?.invitationCode}
          type="number"
          titleProps={{
            children: (
              <Stack direction="row" alignItems="center" spacing={1}>
                <Typography variant="subtitle1" color="text.secondary">
                  {t('invitationCode')}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  ({t('invitationCodeNote')})
                </Typography>
              </Stack>
            ),
          }}
          {...register('invitationCode')}
        />
        <ButtonCell loading={isSubmitting} />
      </Form>
    </Stack>
  )
}

type SocialSignUpCellProps = {
  onSignUpWithApple: () => Promise<void>
  onSignUpWithGoogle: () => Promise<void>
  onSignUpWithTwitter: () => Promise<void>
}

const SocialSignUpCell: Cell<SocialSignUpCellProps> = ({
  onSignUpWithTwitter,
  onSignUpWithApple,
  onSignUpWithGoogle,
}) => {
  return (
    <CellFrame>
      <Stack justifyContent="center" direction="row" spacing={5} sx={{ py: 3 }}>
        <IconButton.Google
          sx={{ bgcolor: config.theme.dark ? 'white' : 'grey.50' }}
          onClick={onSignUpWithGoogle}
        />
        {getPlatform() !== 'android' && (
          <IconButton.Apple
            sx={{ bgcolor: config.theme.dark ? 'white' : 'grey.50' }}
            onClick={onSignUpWithApple}
            isDark={config.theme.dark}
          />
        )}

        <IconButton.Twitter
          sx={{ bgcolor: config.theme.dark ? 'white' : 'grey.50' }}
          onClick={onSignUpWithTwitter}
        />
      </Stack>
    </CellFrame>
  )
}

const SupplementTextCell: Cell = () => {
  const { t } = useTranslation('signUp')

  return (
    <CellFrame>
      <Stack sx={{ py: 3 }}>
        <Divider>
          <Typography sx={{ mx: 3, whiteSpace: 'nowrap' }}>
            {t('or')}
          </Typography>
        </Divider>
      </Stack>
    </CellFrame>
  )
}

const TextInputCell = forwardRef(
  (props: Omit<TextInputFieldProps, 't'>, ref) => {
    const { t } = useTranslation('signUpArtist')

    return (
      <CellFrame>
        <TextInputField {...props} ref={ref} t={t} />
      </CellFrame>
    )
  }
)

const PasswordInputCell = forwardRef(
  (props: Omit<TextInputFieldProps, 't' | 'type'>, ref) => {
    const { t } = useTranslation('signUpArtist')
    const [showPassword, setShowPassword] = useState(false)

    return (
      <CellFrame>
        <TextInputField
          type={showPassword ? 'text' : 'password'}
          {...props}
          textFieldProps={{
            ...props.textFieldProps,
            InputProps: {
              ...props.textFieldProps?.InputProps,
              inputProps: { variant: 'body2', sx: { px: 4, py: 3 } },
              endAdornment: (
                <InputAdornment position="end">
                  {showPassword ? (
                    <IconButton.Display
                      onClick={() => setShowPassword(prevState => !prevState)}
                    />
                  ) : (
                    <IconButton.Hide
                      onClick={() => setShowPassword(prevState => !prevState)}
                    />
                  )}
                </InputAdornment>
              ),
            },
          }}
          ref={ref}
          t={t}
        />
      </CellFrame>
    )
  }
)

type ButtonCellProps = {
  loading: boolean
}
const ButtonCell: Cell<ButtonCellProps> = ({ loading }) => {
  const { t } = useTranslation('signUp')

  return (
    <CellFrame sx={{ py: 2, textAlign: 'center' }}>
      <Button type="submit" size="large" sx={{ width: 244 }} loading={loading}>
        {t('submit')}
      </Button>
    </CellFrame>
  )
}
